// client/src/pages/seo/DigitalOrderingSystemForRestaurantsEn.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Does your restaurant need a digital ordering system?",
  },
  {
    id: "definition",
    label: "What is a digital ordering system for restaurants?",
  },
  {
    id: "types",
    label: "What types of digital ordering exist?",
  },
  {
    id: "expectation",
    label: "Why digital ordering is becoming normal for guests",
  },
  {
    id: "direct",
    label: "Direct ordering or third-party platforms: what is better?",
  },
  {
    id: "qr-ordering",
    label: "QR code ordering: ordering directly from the table",
  },
  {
    id: "workflow",
    label: "What a good digital ordering workflow should look like",
  },
  {
    id: "payment",
    label: "Digital payment and contactless workflows",
  },
  {
    id: "mistakes",
    label: "Common mistakes in digital ordering systems",
  },
  {
    id: "bestplace",
    label: "How Best Place can help with digital ordering",
  },
  {
    id: "faq",
    label: "FAQ about digital ordering systems",
  },
];

const checklist = [
  "Guests often ask about pickup or pre-ordering.",
  "Your restaurant receives orders through phone, messages, or social media.",
  "You want to collect orders in a more structured way.",
  "You want to connect menu, QR code, and ordering more closely.",
  "You want fewer misunderstandings around names, prices, or products.",
  "You may later offer pickup, takeaway, or table ordering digitally.",
  "You want to make add-ons such as drinks, desserts, or sets more visible.",
  "You want the ordering flow to be easier on smartphones.",
  "You want to depend less on third-party platforms only.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "A digital ordering system may not be urgent yet",
      text:
        "If your orders are rare or very simple, a digital menu with clear information may be the better first step.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "A digital ordering flow is worth testing",
      text:
        "Your restaurant could benefit from collecting orders more clearly and connecting menu, QR code, and the next action better.",
      tone: "medium",
    };
  }

  return {
    title: "Digital ordering can become an important next step",
    text:
      "Several points suggest that digital ordering could help your restaurant: less chaos, clearer order data, better mobile experience, and more control.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visual idea">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idea for illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function DigitalOrderingSystemForRestaurantsEn() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title =
      "Digital Ordering System for Restaurants | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/digital-ordering-system-for-restaurants",
          de: "/de/digitales-bestellsystem-gastronomie",
          no: "/no/digitalt-bestillingssystem-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Digital ordering system for restaurants: from menu to order
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                A digital ordering system does not start with payment. It starts
                where guests understand your offer: your menu, QR code,
                smartphone experience, and a clear next step.
              </p>

              <p className="seoHero__text">
                For restaurants, digital ordering can help collect orders more
                clearly, reduce misunderstandings, and make add-ons such as
                drinks, desserts, sets, pickup, or takeaway more visible.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="en" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Content of the article</h2>

        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Does your restaurant need a digital ordering system?</h2>

            <p>
              Press the statements that sound true for your restaurant. The
              result is intentionally honest: not every restaurant needs digital
              ordering immediately, but many restaurants can benefit from a
              clearer menu-to-order flow earlier than they think.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>What is a digital ordering system for restaurants?</h2>

          <p>
            A digital ordering system is a process that allows guests to select
            food or drinks through a website, digital menu, QR code, or mobile
            interface and send an order or structured request.
          </p>

          <p>
            Depending on the restaurant, this can mean pickup, takeaway,
            delivery, pre-ordering, or table ordering. The key point is not just
            the technology. The guest should clearly understand what is
            available, what it costs, and how to order.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Digital menu.</li>
            <li className="text__seo-li">QR code access.</li>
            <li className="text__seo-li">Product selection.</li>
            <li className="text__seo-li">Cart or request.</li>
            <li className="text__seo-li">
              Pickup, delivery, or table ordering.
            </li>
            <li className="text__seo-li">Optional digital payment.</li>
            <li className="text__seo-li">
              Structured order data for the restaurant.
            </li>
          </ul>

          <h3>Ordering should begin with a clear menu</h3>

          <p>
            If the menu is confusing, the ordering process will be confusing too.
            A good digital ordering system needs a clean foundation: products,
            prices, availability, descriptions and a simple mobile flow.
          </p>
        </section>

        <section id="types" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What types of digital ordering exist?</h2>

            <p>
              Not every restaurant needs the same ordering system immediately. A
              café, takeaway business, hotel restaurant, and pizzeria have
              different workflows.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Common order types</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>Takeaway ordering:</strong> Guest orders online and
                    picks up later.
                  </li>
                  <li className="text__seo-li">
                    <strong>Delivery ordering:</strong> Guest orders online and
                    receives delivery.
                  </li>
                  <li className="text__seo-li">
                    <strong>Pre-ordering:</strong> Guest selects time and
                    products before visiting.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Flexible first steps</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>QR code ordering:</strong> Guest scans the code and
                    orders from the table.
                  </li>
                  <li className="text__seo-li">
                    <strong>Structured request:</strong> Restaurant receives an
                    order request instead of instant automation.
                  </li>
                  <li className="text__seo-li">
                    <strong>Menu with order option:</strong> The digital menu
                    becomes the entry point.
                  </li>
                </ul>
              </div>
            </div>

            <p>
              For many restaurants, the best start is not “everything at once”.
              It is a clear first step: digital menu, QR code, understandable
              products, and later an ordering flow.
            </p>
          </div>
        </section>

        <section id="expectation" className="acquaintance">
          <h2>Why digital ordering is becoming normal for guests</h2>

          <p>
            Guests are used to doing things on their smartphones: viewing menus,
            reserving tables, ordering, or paying. This does not mean every
            restaurant has to automate everything. It means that a simple
            digital path is increasingly expected.
          </p>

          <p>
            For Best Place, the important idea is this: digital ordering should
            not be isolated. It should connect to where the guest already is —
            the menu, QR code, restaurant page, or reservation flow.
          </p>

          <h3>Digital ordering should feel like the next step</h3>

          <p>
            A guest first wants to understand the offer. Then, if the next step
            is clear, the guest can send an order, request pickup, reserve a
            table or ask for more information.
          </p>

          <VisualIdea>
            A guest opens a digital menu on a smartphone, checks dishes and
            prices, then moves naturally to a simple order or request button.
          </VisualIdea>
        </section>

        <section id="direct" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Direct ordering or third-party platforms: what is better?</h2>

            <p>
              Third-party platforms can bring reach and can be useful for many
              restaurants. At the same time, it is valuable for restaurants to
              build their own digital path as well.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Third-party platform</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Can bring additional reach.
                  </li>
                  <li className="text__seo-li">
                    Guests may already know the platform.
                  </li>
                  <li className="text__seo-li">
                    Restaurant depends on platform rules and layout.
                  </li>
                  <li className="text__seo-li">
                    Guest relationship may not fully belong to the restaurant.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Own digital ordering path</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    More control over menu and presentation.
                  </li>
                  <li className="text__seo-li">
                    Direct link from website or QR code.
                  </li>
                  <li className="text__seo-li">
                    Better connection with the restaurant brand.
                  </li>
                  <li className="text__seo-li">
                    Can be expanded step by step.
                  </li>
                </ul>
              </div>
            </div>

            <p>
              The best strategy does not have to be either-or. A restaurant can
              use platforms and still build its own digital structure.
            </p>
          </div>
        </section>

        <section id="qr-ordering" className="acquaintance">
          <h2>QR code ordering: ordering directly from the table</h2>

          <p>
            QR code ordering means that guests scan a QR code at the table, open
            the digital menu, and start an order or request from there.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">Scan QR code.</li>
            <li className="text__seo-li">Open menu.</li>
            <li className="text__seo-li">Select products.</li>
            <li className="text__seo-li">Send order.</li>
            <li className="text__seo-li">Restaurant processes it.</li>
          </ol>

          <p>
            This can be especially interesting for concepts where guests often
            want to order more: drinks, desserts, snacks, sets, or another
            round.
          </p>

          <div className="seoCompareSmall">
            <div>
              <strong>Related page</strong>
              <p>
                <Link to="/en/qr-code-menu-for-restaurants">
                  QR code menu
                </Link>
              </p>
            </div>

            <div>
              <strong>Related page</strong>
              <p>
                <Link to="/en/digital-menu-for-restaurants">
                  Digital menu
                </Link>
              </p>
            </div>
          </div>
        </section>

        <section id="workflow" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What a good digital ordering workflow should look like</h2>

            <p>
              A good ordering system is not just a button. It is a clear
              workflow that reduces errors and gives guests confidence.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">
                Guest opens the menu through website or QR code.
              </li>
              <li className="text__seo-li">
                Guest sees categories, prices, photos, and availability.
              </li>
              <li className="text__seo-li">Guest selects products.</li>
              <li className="text__seo-li">
                Guest sees summary and total price.
              </li>
              <li className="text__seo-li">
                Guest chooses pickup, table order, or request.
              </li>
              <li className="text__seo-li">
                Restaurant receives clear order data.
              </li>
              <li className="text__seo-li">
                Team confirms or processes the order.
              </li>
            </ol>

            <p>
              The clearer this flow is, the fewer questions appear. Digital
              ordering should not be more complicated than a phone call — it
              should be clearer.
            </p>
          </div>
        </section>

        <section id="payment" className="acquaintance">
          <h2>Digital payment and contactless workflows</h2>

          <p>
            Not every restaurant needs digital payment immediately. But
            ordering, pickup, and payment belong together in the long term. If a
            guest orders digitally, they often expect a clear next step for
            payment or pickup.
          </p>

          <p>
            For a first version, the restaurant can start with structured
            requests or pre-orders. Later, online payment, QR payment, pickup
            payment or contactless payment can be added depending on the
            restaurant concept.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">
              Structured request without payment.
            </li>
            <li className="text__seo-li">Pickup order with payment on site.</li>
            <li className="text__seo-li">Pre-order with later confirmation.</li>
            <li className="text__seo-li">Digital payment as a later step.</li>
            <li className="text__seo-li">
              Contactless payment for a smoother guest flow.
            </li>
          </ul>

          <h3>Payment does not have to be the first step</h3>

          <p>
            A restaurant can first test whether guests understand the digital
            menu, product selection and ordering flow. Payment can be added once
            the basic process is clear.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Common mistakes in digital ordering systems</h2>

            <p>
              Digital ordering helps only when the flow is clear for guests and
              manageable for the team. If the restaurant starts with too much
              complexity, the system can create new problems instead of solving
              old ones.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Common mistakes</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Ordering is added before the menu is clear.
                  </li>
                  <li className="text__seo-li">
                    There are too many unnecessary steps.
                  </li>
                  <li className="text__seo-li">
                    Sold-out items remain available.
                  </li>
                  <li className="text__seo-li">
                    Pickup or delivery details are unclear.
                  </li>
                  <li className="text__seo-li">
                    The guest receives no clear confirmation.
                  </li>
                  <li className="text__seo-li">
                    The team is not included in the workflow.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Better</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Build the digital menu foundation first.
                  </li>
                  <li className="text__seo-li">
                    Keep the ordering flow short.
                  </li>
                  <li className="text__seo-li">
                    Hide unavailable items immediately.
                  </li>
                  <li className="text__seo-li">
                    Explain pickup, table order or request clearly.
                  </li>
                  <li className="text__seo-li">
                    Show a clear confirmation after sending.
                  </li>
                  <li className="text__seo-li">
                    Train the team on how to process requests.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="en" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Frequently asked questions about digital ordering systems</h2>

          <div className="seoFaq">
            <details>
              <summary>Does every restaurant need a digital ordering system?</summary>
              <p>
                No. If you do not offer pickup, delivery, or table ordering, a
                digital menu may be the more important first step.
              </p>
            </details>

            <details>
              <summary>Is digital ordering only for delivery?</summary>
              <p>
                No. It can also be used for pickup, pre-ordering, or table
                ordering.
              </p>
            </details>

            <details>
              <summary>Can QR code ordering replace service?</summary>
              <p>
                Not necessarily. In many restaurants, it supports service, for
                example for additional drinks, desserts, or repeat orders.
              </p>
            </details>

            <details>
              <summary>Is direct ordering better than using a platform?</summary>
              <p>
                Not always. Platforms can bring reach. A direct digital path
                gives more control over brand, menu, and guest relationship.
              </p>
            </details>

            <details>
              <summary>Does payment need to be integrated immediately?</summary>
              <p>
                No. Some restaurants start with structured requests or
                pre-orders and add digital payment later.
              </p>
            </details>

            <details>
              <summary>What should be implemented first?</summary>
              <p>
                A clear digital menu is the foundation. Without a good menu
                structure, ordering becomes harder too.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="en"
          group="business"
          exclude="/en/digital-ordering-system-for-restaurants"
        />
      </article>
    </main>
  );
}